import { useCallback } from 'react';

import { Locations } from '@/api/Locations';
import {
  LocationFloorMapsRequest,
  LocationRequest,
  LocationRequestDataWithFiles,
  LocationResponse,
  LocationTargetResponse,
  LocationTargetUpdateRequest,
  PageResponseLocationResponse,
  PageResponseLocationWithSpacesResponse,
  PersonOfContactResponse,
  PostgresFilePointer,
} from '@/api/data-contracts';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

const useLocationsService = () => {
  const locationApi = useUploadProgressService(Locations);

  const getLocations = async (
    query: {
      request: LocationRequest;
    },
    signal: AbortSignal | undefined,
  ): Promise<PageResponseLocationResponse> => {
    const { data } = await locationApi.listLocations(query.request, {
      signal,
    });
    return data;
  };
  const getLocationsWithSpaces = async (
    query: {
      request: LocationRequest;
    },
    signal: AbortSignal | undefined,
  ): Promise<PageResponseLocationWithSpacesResponse> => {
    const { data } = await locationApi.listLocationsWithSpaces(query.request, {
      signal,
    });
    return data;
  };

  const getLocationDetails = async (
    id: string,
    signal: AbortSignal | undefined,
  ): Promise<LocationResponse> => {
    const { data } = await locationApi.getLocationById(id, { signal });
    return data;
  };

  const createLocation = async (form: LocationRequestDataWithFiles): Promise<LocationResponse> => {
    const { data } = await locationApi.addLocation(form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const updateLocation = async (
    locationId: string,
    form: LocationRequestDataWithFiles,
  ): Promise<LocationResponse> => {
    const { data } = await locationApi.updateLocation(locationId, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const deleteLocation = async (locationId: string): Promise<LocationResponse> => {
    const { data } = await locationApi.deleteLocation(locationId);
    return data;
  };

  const downloadDocumentById = useCallback(
    async (locationId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await locationApi.downloadLocationDocument(locationId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [locationApi],
  );

  const getLocationCities = async (
    signal?: AbortSignal | undefined,
  ): Promise<Record<string, string[]>> => {
    const { data } = await locationApi.listCities({
      signal,
      body: {
        data: { numberOfItems: 500 },
      },
    });

    return data;
  };

  const getLocationFloorMaps = async (
    locationId: string,
    signal?: AbortSignal | undefined,
  ): Promise<Record<string, PostgresFilePointer[]>> => {
    const { data } = await locationApi.getLocationFloorMaps(locationId, {
      signal,
    });

    return data;
  };

  const downloadFloorMaps = useCallback(
    async (
      locationId: string,
      documentId: string,
      signal?: AbortSignal | undefined,
    ): Promise<File> => {
      const { data } = await locationApi.downloadFloorMaps(locationId, documentId, undefined, {
        signal,
        format: 'blob',
      });

      return data;
    },
    [locationApi],
  );

  const updateLocationFloorMaps = async (
    locationId: string,
    request: LocationFloorMapsRequest,
  ): Promise<Record<string, PostgresFilePointer[]>> => {
    const { data } = await locationApi.updateLocationFloorMaps(locationId, request, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return data;
  };

  const deleteLocationFloorMaps = async (
    locationId: string,
    documentId: string,
  ): Promise<Record<string, PostgresFilePointer[]>> => {
    const { data } = await locationApi.deleteLocationFloorMaps(locationId, documentId);

    return data;
  };

  const getLocationTargets = async (
    locationIds?: string[],
    signal?: AbortSignal,
  ): Promise<LocationTargetResponse> => {
    const { data } = await locationApi.getLocationTargets(
      { locationIds: locationIds?.join(',') },
      { signal },
    );
    return data;
  };

  const updateLocationTargets = async (
    request: LocationTargetUpdateRequest,
  ): Promise<LocationTargetResponse> => {
    const { data } = await locationApi.updateLocationTargets(request);
    return data;
  };

  const getContactCandidates = async (
    companyId: string,
    signal?: AbortSignal,
  ): Promise<PersonOfContactResponse[]> => {
    const { data } = await locationApi.getContactCandidates({ companyId }, { signal });
    return data;
  };

  return {
    getLocationsWithSpaces,
    getLocations,
    getLocationDetails,
    createLocation,
    updateLocation,
    deleteLocation,
    downloadDocumentById,
    getLocationCities,
    getLocationFloorMaps,
    downloadFloorMaps,
    updateLocationFloorMaps,
    deleteLocationFloorMaps,
    getLocationTargets,
    updateLocationTargets,
    getContactCandidates,
  };
};

export default useLocationsService;
